import { useState, type ReactNode } from "react";

/** Shared bits of Saúde, Finanças and Ciclo (styled in life.css). */

export type Tone = "success" | "warning" | "danger" | "accent" | "muted";

/** Small status pill: tinted fill, colored text. */
export function StatusChip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`chip ${tone}`}>{children}</span>;
}

/** Segmented control (the agenda's view switch grammar) used as in-page tabs. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; count?: number; tone?: "danger" }>;
  label: string;
}) {
  return (
    <div className="seg life-seg" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={value === o.value} className={`seg-opt${value === o.value ? " on" : ""}`} onClick={() => onChange(o.value)}>
          {o.label}
          {(o.count ?? 0) > 0 && <span className={`seg-count${o.tone ? ` ${o.tone}` : ""}`}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** A tab choice remembered in this browser (storage may be missing or blocked). */
export function useStoredTab<T extends string>(key: string, values: readonly T[]): [T, (v: T) => void] {
  const [tab, setTab] = useState<T>(() => {
    try {
      const v = localStorage.getItem(key) as T | null;
      return v && values.includes(v) ? v : values[0];
    } catch {
      return values[0];
    }
  });
  const set = (v: T) => {
    setTab(v);
    try {
      localStorage.setItem(key, v);
    } catch {
      // Only the remembered tab is lost.
    }
  };
  return [tab, set];
}
